import {
  ActionName,
  CategoryActionP,
  CategoryName,
  ConditionSetP,
  DisplayModeName,
  DomainsDefinition,
  IApplicationSchema,
  ICategoryParsed,
  IContextMenuParsed,
  IDisplayModeParsed,
  IFormParsed,
  IMainMenuParsed,
  IViewParsed,
  processSchema,
  schemasDecorators,
  SchemaType,
  TFormName,
  tg,
  TViewName,
} from '@infotech-soe/metaschema';

import runInSandbox from '../../../framework/system/runInSandbox';

import ISchemaParser, { ParseCategoryResult } from './ISchemaParser';

/**
 * {@link ISchemaParser} implementation for metaschema.
 */
class MetaschemaParser implements ISchemaParser {
  public parseAction(
    _category: CategoryName,
    action: ActionName,
    source: string
  ): CategoryActionP {
    const schema = this.parseSchema(action, source, 'action');

    if (schema.constructor.name === 'Action') {
      return schema;
    }

    const actionSchema = schema[action];

    if (!actionSchema) {
      throw new Error('Cannot parse schema as action');
    }

    return actionSchema;
  }

  public parseCategory(
    category: CategoryName,
    source: string
  ): ParseCategoryResult {
    const schema = this.parseSchema(
      category,
      source,
      'category'
    ) as ICategoryParsed;

    const actions = [];

    for (const key of Object.keys(schema)) {
      const value = schema[key];

      if (
        typeof value === 'object' &&
        (value.constructor.name === 'Action' ||
          value.constructor.name === 'DeleteAction' ||
          value.constructor.name === 'ShowCategory' ||
          value.constructor.name === 'Print' ||
          tg.isConstructedBy(value, 'SignAction'))
      ) {
        actions.push({
          name: key,
          schema: value as CategoryActionP,
        });
        delete schema[key];
      }
    }

    return {
      category: schema,
      actions,
    };
  }

  public parseDisplayMode(
    _category: CategoryName,
    displayMode: DisplayModeName,
    source: string
  ): IDisplayModeParsed {
    return this.parseSchema(displayMode, source, 'displayMode');
  }

  public parseDomains(schemaName: string, source: string): DomainsDefinition {
    return this.parseSchema(schemaName, source, 'domains');
  }

  public parseForm(
    _category: CategoryName,
    form: TFormName,
    source: string
  ): IFormParsed {
    return this.parseSchema(form, source, 'form');
  }

  public parseApplication(
    application: string,
    source: string
  ): IApplicationSchema {
    return this.parseSchema(application, source, 'application');
  }
  public parseMenu(application: string, source: string): IMainMenuParsed {
    return this.parseSchema(application, source, 'mainMenu');
  }

  public parseView(view: TViewName, source: string): IViewParsed {
    return this.parseSchema(view, source, 'view');
  }

  public parseTemplate(name: string, source: string): IContextMenuParsed {
    return this.parseSchema(name, source, 'contextMenu');
  }

  public parseConditionsSet(name: string, source: string): ConditionSetP {
    return this.parseSchema(name, source, 'conditions');
  }

  private parseSchema(filename: string, source: string, type: SchemaType): any {
    return processSchema(
      filename,
      source,
      { ...schemasDecorators[type] },
      runInSandbox
    );
  }
}

export default MetaschemaParser;
